import { DANGER, GREEN } from '../Theme/palette';
import {
  CameraOutlined,
  CheckCircleFilled,
  CloseOutlined,
  DisconnectOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
  ReloadOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  DrawerCloseButton,
  inspectionStatusColour,
  InspectionStatus,
  RegPlate,
  StatusRadioGroup,
  UnsavedChangesModal,
} from '@components';
import {
  InspectionPhoto,
  MAX_PHOTOS_PER_ITEM,
  useInspection,
  useInspectionAutosave,
  useInspectionPhotos,
  useOnlineStatus,
  useView,
} from '@hooks';
import {
  claimInspection,
  CreateInspectionResponse,
  InspectionSubCategory,
  releaseInspection,
} from '@services';
import {
  Alert,
  Button,
  Collapse,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Progress,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const { TextArea } = Input;
const { Text } = Typography;

interface IProps {
  open: boolean;
  inspectionId: string | undefined;
  onCancel: () => void;
}

/** Status per checklist item, kept aside from the form so typing a note does not redraw the counters. */
type StatusByItem = Record<number, InspectionStatus | null>;

/**
 * Filling in a vehicle health check.
 *
 * A drawer rather than a modal because this is half an hour of work against a vehicle, not a dialog:
 * it wants the width for an item and its answer to sit on one line, and it leaves the job card it was
 * opened from in view.
 *
 * Each item saves as it is answered and each photo uploads as it is taken - see useInspectionAutosave
 * and useInspectionPhotos. Nothing here waits for a Save button.
 */
export const InspectionFormDrawer: React.FC<IProps> = observer(
  ({ open, inspectionId, onCancel }) => {
    const [form] = Form.useForm();
    const { isMobile } = useView();
    const bodyRef = useRef<HTMLDivElement>(null);
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [statusByItem, setStatusByItem] = useState<StatusByItem>({});
    const [activeSection, setActiveSection] = useState<string>();

    const { loading, inspection } = useInspection(inspectionId);
    const autosave = useInspectionAutosave(inspectionId);
    const { drafts } = autosave;
    const online = useOnlineStatus();
    const photos = useInspectionPhotos(inspectionId, inspection);

    // Populate once per inspection. Keyed on the inspection rather than on every render of it, so a
    // refresh elsewhere cannot rewrite the form on top of what is being typed.
    const populatedForRef = useRef<string | undefined>(undefined);

    const sections = useMemo(
      () =>
        [...(inspection?.category.subCategories ?? [])].sort(
          (a, b) => (a.orderPosition ?? 0) - (b.orderPosition ?? 0)
        ),
      [inspection]
    );

    useEffect(() => {
      if (!open) {
        // Cleared alongside the populate guard, so reopening cannot flash the previous inspection's
        // counters in the header before the new one loads.
        populatedForRef.current = undefined;
        setStatusByItem({});
        return;
      }

      // Drafts are read from the device before the form is filled in, not after: painting the
      // server's version first and correcting it a moment later is how someone comes back to an
      // inspection, sees their work missing, and types it again.
      if (!inspection || drafts === undefined || populatedForRef.current === inspection.id) return;

      populatedForRef.current = inspection.id;

      const seeded: StatusByItem = {};

      form.setFieldsValue({
        responses: inspection.category.subCategories.flatMap((sub) =>
          sub.items.map((item) => {
            const match = inspection.responses?.find((r) => r.subCategoryItemId === item.id);
            // An answer waiting on the device is newer than the server's by definition - it is there
            // because the server never got it - and it carries the whole item, so it wins outright.
            const draft = drafts.get(item.id);

            seeded[item.id] = ((draft ? draft.status : match?.status) as InspectionStatus) ?? null;

            return {
              subCategoryItemId: item.id,
              status: (draft ? draft.status : match?.status) ?? null,
              depth: (draft ? draft.depth : match?.depth) ?? null,
              note: (draft ? draft.note : match?.note) ?? '',
            };
          })
        ),
      });

      setStatusByItem(seeded);

      if (drafts.size > 0)
        message.info(
          `${drafts.size} unsent ${drafts.size === 1 ? 'change' : 'changes'} restored from this device.`
        );

      // Open the first section with anything left to answer, so picking an inspection back up lands
      // where the work stopped rather than at the top every time.
      const sorted = [...inspection.category.subCategories].sort(
        (a, b) => (a.orderPosition ?? 0) - (b.orderPosition ?? 0)
      );
      const next = sorted.find((sub) => sub.items.some((item) => !seeded[item.id]));
      const opening = next ?? sorted[0];

      setActiveSection(opening ? String(opening.id) : undefined);
    }, [open, inspection, drafts, form]);

    // While this is open it owns the queue for this inspection and reports on it. The background sync
    // steps aside rather than sending the same items underneath a screen that is showing its own
    // state - both would be safe, since the API upserts, but the screen would be describing a race.
    useEffect(() => {
      if (!open || !inspectionId) return;

      claimInspection(inspectionId);

      return () => {
        releaseInspection(inspectionId);
      };
    }, [open, inspectionId]);

    const unsaved = autosave.hasUnsaved || photos.uploadingCount > 0 || photos.failedCount > 0;

    // Only worth stopping someone when closing would actually cost them something. With a local copy
    // being kept, a refresh loses nothing - it comes back and sends itself - and a prompt that cries
    // wolf teaches people to click through the one that matters.
    useEffect(() => {
      if (!open || !unsaved || autosave.storageAvailable) return;

      const warn = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = '';
      };

      window.addEventListener('beforeunload', warn);
      return () => window.removeEventListener('beforeunload', warn);
    }, [open, unsaved, autosave.storageAvailable]);

    /**
     * Queues one item. immediate for a choice the user has plainly finished making - a status, a
     * field they have left - and debounced while a note is still being typed.
     */
    const saveItem = useCallback(
      (fieldIndex: number, immediate = false) => {
        const values = form.getFieldValue(['responses', fieldIndex]);
        if (!values?.subCategoryItemId) return;

        const response: CreateInspectionResponse = {
          subCategoryItemId: values.subCategoryItemId,
          status: values.status ?? null,
          depth: values.depth ?? null,
          note: values.note ?? null,
        };

        autosave.queue(values.subCategoryItemId, response, immediate);
      },
      [autosave, form]
    );

    const handleStatusChange = useCallback(
      (fieldIndex: number, subCategoryItemId: number, status: InspectionStatus | null) => {
        // Written to the form here rather than left to the control: coming from the status buttons
        // the form already has it, but the clear button calls straight in, and without this the
        // buttons would keep showing the old choice and the save would carry it.
        form.setFieldValue(['responses', fieldIndex, 'status'], status);

        setStatusByItem((current) => ({ ...current, [subCategoryItemId]: status }));

        saveItem(fieldIndex, true);
      },
      [form, saveItem]
    );

    const handleSectionChange = useCallback(
      (key: string | string[]) => {
        // Leaving a section is as clear a "done with that" as there is: send whatever is still
        // sitting in the debounce rather than waiting it out.
        void autosave.flush();

        setActiveSection(Array.isArray(key) ? key[0] : key);

        if (bodyRef.current) bodyRef.current.scrollTop = 0;
      },
      [autosave]
    );

    const answered = useMemo(
      () => Object.values(statusByItem).filter(Boolean).length,
      [statusByItem]
    );
    const totalItems = useMemo(
      () => sections.reduce((count, sub) => count + sub.items.length, 0),
      [sections]
    );

    /**
     * With no connection there is nothing to warn about: the work is on the device and goes up by
     * itself. Warning here would teach people that closing loses things, which is the fear this
     * whole screen exists to remove.
     */
    const closeWithDraftKept = useCallback(() => {
      const waiting = autosave.pendingCount + photos.uploadingCount + photos.failedCount;

      if (waiting > 0)
        message.info(
          `${waiting} ${waiting === 1 ? 'change is' : 'changes are'} saved on this device and will be sent when you are back online.`
        );

      setOpenCancelModal(false);
      onCancel();
    }, [autosave.pendingCount, onCancel, photos.failedCount, photos.uploadingCount]);

    const handleDone = useCallback(async () => {
      if (!online) {
        closeWithDraftKept();
        return;
      }

      const saved = await autosave.flush();

      if (!saved || photos.uploadingCount > 0 || photos.failedCount > 0) {
        setOpenCancelModal(true);
        return;
      }

      setOpenCancelModal(false);
      onCancel();
    }, [autosave, closeWithDraftKept, onCancel, online, photos.failedCount, photos.uploadingCount]);

    /** From the warning modal: try once more, then close either way - they have been told. */
    const handleSaveAndClose = useCallback(async () => {
      await autosave.flush();
      setOpenCancelModal(false);
      onCancel();
    }, [autosave, onCancel]);

    const handleClose = useCallback(() => {
      if (!online) {
        closeWithDraftKept();
        return;
      }

      if (unsaved) {
        setOpenCancelModal(true);
        return;
      }

      onCancel();
    }, [closeWithDraftKept, onCancel, online, unsaved]);

    return (
      <>
        <Drawer
          open={open}
          onClose={handleClose}
          // 800 is what the widest drawer in the portal uses. Wider only pays off if the note moves
          // onto the same line as the name and status; as it is, the extra width lands in the gap
          // between the two, and every pixel here is one off the job card behind.
          width={isMobile ? '100%' : 'min(800px, 100vw)'}
          styles={{ wrapper: { maxWidth: '100vw' }, body: { padding: 0 } }}
          placement="right"
          destroyOnHidden
          closeIcon={false}
          loading={loading}
          title={
            <Header
              category={inspection?.category.name}
              vrm={inspection?.vrm}
              answered={answered}
              total={totalItems}
            />
          }
          extra={<DrawerCloseButton onClick={handleClose} />}
          footer={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <SaveIndicator
                online={online}
                state={autosave.state}
                lastSavedAt={autosave.lastSavedAt}
                pendingCount={autosave.pendingCount}
                uploadingCount={photos.uploadingCount}
                failedCount={photos.failedCount}
                onRetryPhotos={photos.retryAllFailed}
              />
              <Button type="primary" onClick={handleDone} loading={autosave.state === 'saving'}>
                Done
              </Button>
            </div>
          }
        >
          <div ref={bodyRef} style={{ height: '100%', overflowY: 'auto', padding: 16 }}>
            {!autosave.storageAvailable && (
              // Said up front rather than at the point it matters: by the time a connection drops,
              // the half hour has already been spent.
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="This browser is not keeping a local copy"
                description="Site data is blocked or full, so your work is only held in this tab. Keep it open until everything has been saved."
              />
            )}

            {!online && (
              // Said once, here, rather than as a toast per failed request - offline for an hour
              // would be a hundred of those. The point is not that something failed but that it is
              // being kept, and that carrying on is fine.
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="No connection"
                description="Keep going - everything is being saved on this device and will be sent as soon as you are back online."
              />
            )}

            <Form form={form} layout="vertical">
              <Form.List name="responses">
                {(fields) => {
                  // The form is a flat list; the checklist is grouped. Map item id to its field once
                  // rather than guessing that the two orders still line up.
                  const indexByItemId = new Map<number, number>();

                  fields.forEach((field) => {
                    const itemId = form.getFieldValue(['responses', field.name, 'subCategoryItemId']);
                    if (itemId) indexByItemId.set(itemId, field.name);
                  });

                  const items = sections.map((sub, sectionIndex) => ({
                    key: String(sub.id),
                    label: <SectionLabel section={sub} statusByItem={statusByItem} />,
                    children: (
                      <>
                        {sub.items.map((item) => {
                          const fieldIndex = indexByItemId.get(item.id);

                          if (fieldIndex === undefined) return null;

                          return (
                            <ItemRow
                              key={item.id}
                              name={item.name}
                              showDepth={item.showDepth}
                              fieldIndex={fieldIndex}
                              status={statusByItem[item.id] ?? null}
                              photos={photos.photosByItem.get(item.id) ?? []}
                              onStatusChange={(status) =>
                                handleStatusChange(fieldIndex, item.id, status)
                              }
                              onSave={(immediate) => saveItem(fieldIndex, immediate)}
                              onAddPhotos={(files) => photos.add(item.id, files)}
                              onRemovePhoto={photos.remove}
                              onRetryPhoto={photos.retry}
                            />
                          );
                        })}

                        {sections[sectionIndex + 1] && (
                          <div style={{ textAlign: 'right', marginTop: 4 }}>
                            <Button
                              type="link"
                              onClick={() => handleSectionChange(String(sections[sectionIndex + 1].id))}
                            >
                              Next: {sections[sectionIndex + 1].name} →
                            </Button>
                          </div>
                        )}
                      </>
                    ),
                  }));

                  return (
                    <Collapse
                      accordion
                      bordered={false}
                      activeKey={activeSection}
                      onChange={handleSectionChange}
                      items={items}
                      style={{ background: 'transparent' }}
                    />
                  );
                }}
              </Form.List>
            </Form>
          </div>
        </Drawer>

        <UnsavedChangesModal
          loading={autosave.state === 'saving'}
          open={openCancelModal}
          onOk={handleSaveAndClose}
          onLeave={() => {
            // Discard has to mean discard: without this the queue would still be on the device and
            // would come back, unasked, the next time the inspection was opened.
            autosave.discard();
            setOpenCancelModal(false);
            onCancel();
          }}
          onCancel={() => setOpenCancelModal(false)}
        />
      </>
    );
  }
);

const Header: React.FC<{
  category?: string;
  vrm?: string;
  answered: number;
  total: number;
}> = ({ category, vrm, answered, total }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 8 }}>
    {/* The plate sits beside the title rather than on a line of its own: the vehicle is already named
        on the panel this opens over, so it is here to confirm, not to inform. Centred rather than
        baseline aligned - the plate is a box, and baseline hangs it low against the heading. */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <Text strong style={{ fontSize: 16 }}>
        {category}
      </Text>

      {vrm && <RegPlate size="small">{vrm}</RegPlate>}
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Progress
        percent={total ? Math.round((answered / total) * 100) : 0}
        showInfo={false}
        size="small"
        strokeColor={GREEN.badge}
        style={{ margin: 0, flex: 1 }}
      />
      <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, whiteSpace: 'nowrap' }}>
        {answered}/{total} answered
      </Text>
    </div>
  </div>
);

const SectionLabel: React.FC<{ section: InspectionSubCategory; statusByItem: StatusByItem }> = ({
  section,
  statusByItem,
}) => {
  const answered = section.items.filter((item) => statusByItem[item.id]).length;
  const complete = answered === section.items.length && answered > 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <Text strong>{section.name}</Text>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {/* One dot per item, in the colour of its answer: the shape of the section at a glance. */}
        <div style={{ display: 'flex', gap: 3 }}>
          {section.items.map((item) => (
            <span
              key={item.id}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: inspectionStatusColour(statusByItem[item.id]) ?? '#e0e0e0',
              }}
            />
          ))}
        </div>

        <Text type={complete ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
          {answered}/{section.items.length}
        </Text>
      </div>
    </div>
  );
};

const ItemRow: React.FC<{
  name: string;
  showDepth: boolean;
  fieldIndex: number;
  status: InspectionStatus | null;
  photos: InspectionPhoto[];
  onStatusChange: (status: InspectionStatus | null) => void;
  onSave: (immediate?: boolean) => void;
  onAddPhotos: (files: File[]) => void;
  onRemovePhoto: (key: string) => void;
  onRetryPhoto: (key: string) => void;
}> = ({
  name,
  showDepth,
  fieldIndex,
  status,
  photos,
  onStatusChange,
  onSave,
  onAddPhotos,
  onRemovePhoto,
  onRetryPhoto,
}) => (
  <div
    style={{
      padding: '10px 12px',
      marginBottom: 8,
      borderRadius: 8,
      background: '#fff',
      border: '1px solid #f0f0f0',
      // Answered items carry their colour down the edge, so scrolling shows what is left.
      borderLeft: `3px solid ${inspectionStatusColour(status) ?? '#f0f0f0'}`,
    }}
  >
    <Form.Item hidden name={[fieldIndex, 'subCategoryItemId']}>
      <Input type="hidden" />
    </Form.Item>

    {/* Name and answer on one line - the checklist is forty-odd items and every row that wraps is
        another scroll between the technician and the end of it. */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
      <Text strong style={{ flex: 1, minWidth: 0 }}>
        {name}
      </Text>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {showDepth && (
          <Form.Item name={[fieldIndex, 'depth']} noStyle>
            <InputNumber
              placeholder="mm"
              style={{ width: 76 }}
              onBlur={() => onSave(true)}
            />
          </Form.Item>
        )}

        <Form.Item name={[fieldIndex, 'status']} noStyle>
          <StatusRadioGroup onChange={onStatusChange} />
        </Form.Item>

        {/* Pressing the chosen status again also clears it, but nothing on screen says so. This only
            takes up room once there is something to clear. */}
        <Button
          type="text"
          size="small"
          icon={<UndoOutlined />}
          title="Clear"
          onClick={() => onStatusChange(null)}
          style={{ visibility: status ? 'visible' : 'hidden' }}
        />
      </div>
    </div>

    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
      <Form.Item name={[fieldIndex, 'note']} noStyle>
        <TextArea
          placeholder="Add a note"
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ flex: 1 }}
          onChange={() => onSave()}
          onBlur={() => onSave(true)}
        />
      </Form.Item>

      <PhotoPicker
        disabled={photos.length >= MAX_PHOTOS_PER_ITEM}
        onPick={onAddPhotos}
      />
    </div>

    {photos.length > 0 && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {photos.map((photo) => (
          <PhotoThumbnail
            key={photo.key}
            photo={photo}
            onRemove={() => onRemovePhoto(photo.key)}
            onRetry={() => onRetryPhoto(photo.key)}
          />
        ))}
      </div>
    )}
  </div>
);

const PhotoPicker: React.FC<{ disabled: boolean; onPick: (files: File[]) => void }> = ({
  disabled,
  onPick,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          files.length && onPick(files);
          // Picking the same file twice in a row still has to fire a change.
          event.target.value = '';
        }}
      />
      <Button
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        icon={<CameraOutlined />}
        title={disabled ? 'Up to 4 photos per item' : 'Add photo'}
      />
    </>
  );
};

const PhotoThumbnail: React.FC<{
  photo: InspectionPhoto;
  onRemove: () => void;
  onRetry: () => void;
}> = ({ photo, onRemove, onRetry }) => (
  <div
    style={{
      width: 64,
      height: 64,
      borderRadius: 8,
      overflow: 'hidden',
      border: '1px solid #e6e6e6',
      position: 'relative',
      background: '#fafafa',
    }}
  >
    <Image
      src={photo.src}
      alt={photo.name}
      width={64}
      height={64}
      style={{ objectFit: 'cover' }}
      preview={photo.status === 'done'}
    />

    {photo.status === 'uploading' && (
      <div style={overlayStyle('rgba(0,0,0,0.45)')}>
        <Progress
          type="circle"
          percent={photo.percent}
          size={36}
          strokeColor="#fff"
          trailColor="rgba(255,255,255,0.3)"
          format={(percent) => (
            <span style={{ color: '#fff', fontSize: 10 }}>{`${percent ?? 0}%`}</span>
          )}
        />
      </div>
    )}

    {photo.status === 'error' && (
      <div style={overlayStyle('rgba(207,19,34,0.55)')}>
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined style={{ color: '#fff', fontSize: 18 }} />}
          onClick={onRetry}
          title="Upload failed - try again"
        />
      </div>
    )}

    <button
      type="button"
      onClick={onRemove}
      aria-label="Remove photo"
      style={{
        position: 'absolute',
        top: 2,
        right: 2,
        width: 18,
        height: 18,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.55)',
        color: '#fff',
        fontSize: 10,
        cursor: 'pointer',
      }}
    >
      <CloseOutlined />
    </button>
  </div>
);

const overlayStyle = (background: string): React.CSSProperties => ({
  position: 'absolute',
  inset: 0,
  background,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/** Where the toast on every save used to be. Quiet unless there is something to say. */
const SaveIndicator: React.FC<{
  online: boolean;
  state: string;
  lastSavedAt?: Date;
  pendingCount: number;
  uploadingCount: number;
  failedCount: number;
  onRetryPhotos: () => void;
}> = ({ online, state, lastSavedAt, pendingCount, uploadingCount, failedCount, onRetryPhotos }) => {
  // Offline outranks the rest: "could not save" is true but useless when the reason is known and
  // nothing is at risk.
  if (!online) {
    const waiting = pendingCount + uploadingCount + failedCount;

    return (
      <Text type="warning">
        <DisconnectOutlined style={{ marginRight: 6 }} />
        {waiting > 0
          ? `Offline - ${waiting} ${waiting === 1 ? 'change' : 'changes'} saved on this device`
          : 'Offline'}
      </Text>
    );
  }

  if (failedCount > 0)
    return (
      <span>
        <ExclamationCircleFilled style={{ color: DANGER.active, marginRight: 6 }} />
        <Text type="danger">
          {failedCount} {failedCount === 1 ? 'photo failed' : 'photos failed'}
        </Text>
        <Button type="link" size="small" onClick={onRetryPhotos}>
          Retry all
        </Button>
      </span>
    );

  if (uploadingCount > 0)
    return (
      <Text type="secondary">
        <LoadingOutlined style={{ marginRight: 6 }} />
        Uploading {uploadingCount} {uploadingCount === 1 ? 'photo' : 'photos'}...
      </Text>
    );

  if (state === 'saving')
    return (
      <Text type="secondary">
        <LoadingOutlined style={{ marginRight: 6 }} />
        Saving...
      </Text>
    );

  if (state === 'error')
    return (
      <Text type="danger">
        <ExclamationCircleFilled style={{ marginRight: 6 }} />
        Could not save - it will retry
      </Text>
    );

  if (state === 'saved' && lastSavedAt)
    return (
      <Text type="secondary">
        <CheckCircleFilled style={{ color: GREEN.badge, marginRight: 6 }} />
        Saved {dayjs(lastSavedAt).format('HH:mm')}
      </Text>
    );

  return <span />;
};
